import type {
  IAutoQuote,
  ERegionCode,
  IRentersQuote,
  TPolicy,
} from '@sureapp/platform-sdk';

import {
  isAutoPolicy,
  isPolicy,
  isQuote,
  isAutoQuote,
} from '@@/common/services/typeGuards';

/**
 * Region code only makes sense in certain contexts. For example, when looking
 * at a policy we can determine the region on that policy. Same with quotes.
 * On certain pages however there isn't a concept of a region code (i.e., on
 * /my-account where several policies could be shown on screen).
 */
export const emptyRegionCode = '';
export type EOptionalRegionCode = ERegionCode | typeof emptyRegionCode;

function getRegionCodeFromPolicy(p: TPolicy | null) {
  const defaultValue = p?.rating_address.region.code ?? emptyRegionCode;

  return isAutoPolicy(p)
    ? p?.details.region_config_code ?? defaultValue
    : defaultValue;
}

function getRegionCodeFromQuote(q: IAutoQuote | IRentersQuote | null) {
  const defaultValue =
    q?.application.rating_address.region.code ?? emptyRegionCode;

  return isAutoQuote(q)
    ? q?.application.region_config_code ?? defaultValue
    : defaultValue;
}

/**
 * Determines the region to use for business logic from a quote or a policy.
 * This is necessary because we don't always want the rating address to
 * determine business logic.
 *
 * @param quoteOrPolicy A quote or policy object to pull a region from.
 * @returns A two-letter state abbreviation (e.g., `ERegionCode`) if found,
 * otherwise an empty string.
 */
export function getRegionCode(
  quoteOrPolicy?: any
): EOptionalRegionCode | typeof emptyRegionCode {
  if (isPolicy(quoteOrPolicy)) {
    return getRegionCodeFromPolicy(quoteOrPolicy);
  }

  if (isQuote(quoteOrPolicy)) {
    return getRegionCodeFromQuote(quoteOrPolicy);
  }

  return emptyRegionCode;
}
